/**
 * Free / paid sticker packs. A paid (premium) pack is free for paying Plus subscribers (not trials)
 * and bought with tokens by everyone else; a purchase is permanent, Plus access ends with the
 * subscription. Every
 * sticker in a paid pack is premium; a sticker has no setting of its own, so moving it into or out
 * of a paid pack changes it.
 */

export type PackPricing = { isPremium?: boolean; priceTokens?: number | null };

/** Mirrors the backend: a whole number from 1 to 1,000,000. */
export const MAX_PACK_PRICE_TOKENS = 1_000_000;

export const packPriceLabel = (pack: PackPricing): string =>
  pack.isPremium && pack.priceTokens ? `Premium · ${pack.priceTokens.toLocaleString()} tokens` : 'Free';

/** Null when valid, otherwise the message to show. */
export function validatePackPrice(raw: string): string | null {
  const value = Number(raw);
  if (raw.trim() === '' || !Number.isInteger(value) || value < 1 || value > MAX_PACK_PRICE_TOKENS) {
    return `Price must be a whole number of tokens from 1 to ${MAX_PACK_PRICE_TOKENS.toLocaleString()}.`;
  }
  return null;
}

/**
 * What an admin must confirm before changing a pack's pricing, or null when nothing needs confirming.
 * Every such change reaches phones on their next sticker sync.
 */
export function pricingChangeWarning(
  before: PackPricing & { purchaseCount?: number },
  after: { isPremium: boolean; priceTokens: number | null }
): string | null {
  const wasPremium = before.isPremium === true;
  const buyers = before.purchaseCount ?? 0;
  if (!wasPremium && after.isPremium) {
    return `Make this pack paid at ${after.priceTokens} tokens?\n\nPaying Plus subscribers keep it for free. Everyone else who uses this pack now loses it until they buy it.`;
  }
  if (wasPremium && !after.isPremium) {
    return `Make this pack free?\n\nIt unlocks for everyone. The ${buyers} purchase record${buyers === 1 ? ' is' : 's are'} kept, and those users keep the pack if it becomes paid again.`;
  }
  if (wasPremium && after.isPremium && before.priceTokens !== after.priceTokens) {
    return `Change the price from ${before.priceTokens} to ${after.priceTokens} tokens?\n\nThe ${buyers} user${buyers === 1 ? ' who has' : 's who have'} already bought it keep it. Only new buyers pay the new price.`;
  }
  return null;
}
